// ==========================================
// FILE: src/stores/authStore.ts
// PURPOSE: Zustand store for admin authentication state
// ==========================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AdminUser {
  adminId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  adminRole: 'SUPER_ADMIN' | 'ADMIN' | 'CUSTOMER_SUPPORT' | 'MODERATOR' | 'ANALYST' | 'FINANCE';
  permissions: string[];
  department?: string;
  status: 'active' | 'inactive' | 'suspended';
  profileImage?: string;
  lastLoginAt?: string;
  createdAt: string;
}

interface AuthState {
  admin: AdminUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAdmin: (admin: AdminUser | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      setAdmin: (admin) =>
        set({
          admin,
          isAuthenticated: !!admin,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),

      setLoading: (isLoading) =>
        set({ isLoading }),

      logout: () =>
        set({
          admin: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),

      hasPermission: (permission) => {
        const { admin } = get();
        if (!admin) return false;
        if (admin.adminRole === 'SUPER_ADMIN') return true;
        return admin.permissions.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get();
        return permissions.every((p) => hasPermission(p));
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        admin: state.admin,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);